import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';
import bg2Import from '../../images/background/bg1.png';

// Elements
import AvailableCitiesModal from '../elements/available-cities-modal';

// Images
import bnrImgImport from '../../images/banner/img_aboutbnr.webp';

const bg2 = typeof bg2Import === 'object' ? bg2Import.default : bg2Import;
const bnrImg = typeof bnrImgImport === 'object' ? bnrImgImport.default : bnrImgImport;

class DeleteAccount extends Component {
  state = {
    isClient: false,
  };

  componentDidMount() {
    this.setState({ isClient: true });
  }

  render() {
    const { isClient } = this.state;

    return (
      <>
        <Helmet>
          <title>Delete Your Account | Ride N Repair</title>
          <meta
            name="description"
            content="Steps to delete your Ride N Repair customer account and a description of the data that is removed, retained, and the retention period."
          />
          <link rel="canonical" href="https://www.ridenrepair.com/delete-account" />
        </Helmet>
        {this.props.citiesModal && (
          <AvailableCitiesModal closeModal={() => this.props.setCitiesModal(false)} />
        )}

        <Header
          user={this.props.user}
          setUser={this.props.setUser}
          isMobileApp={this.props.isMobileApp}
        />

        <div className="page-content bg-white">
          <div
            className="page-banner ovbl-dark"
            style={isClient ? { backgroundImage: 'url(' + bnrImg + ')' } : {}}
          >
            <div className="container">
              <div className="page-banner-entry text-center">
                <h1>
                  <span>Delete Your Account</span>
                </h1>
                <nav aria-label="breadcrumb" className="breadcrumb-row">
                  <ul className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">
                        <i className="las la-home"></i>Home
                      </Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Delete Account
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>

          <section
            className="section-area section-sp1 bg-white"
            style={
              isClient
                ? { backgroundImage: 'url(' + bg2 + ')', backgroundRepeat: 'no-repeat' }
                : {}
            }
          >
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-12 mb-30 mb-md-50">
                  <div className="heading-bx">
                    <h4 className="title-ext text-primary">
                      Delete your Ride N Repair account and data
                    </h4>

                    <p>
                      RIDE N REPAIR PRIVATE LIMITED (&quot;Ride N Repair&quot;, &quot;Company&quot;,
                      &quot;We&quot;) lets every customer delete their account and request removal
                      of their personal data at any time. This page explains how to start a
                      deletion request, what data is removed immediately, what we are legally
                      required to retain, and for how long.
                    </p>

                    <h5 className="mt-4">
                      <strong>How to delete your account from inside the app</strong>
                    </h5>
                    <ol>
                      <li className="terms-element-list">
                        Open the <strong>Ride N Repair</strong> customer app on your phone.
                      </li>
                      <li className="terms-element-list">
                        Tap the <strong>You</strong> tab at the bottom right of the screen.
                      </li>
                      <li className="terms-element-list">
                        Scroll to the bottom of the screen until you see the{' '}
                        <strong>DANGER</strong> section.
                      </li>
                      <li className="terms-element-list">
                        Tap <strong>Delete account</strong>.
                      </li>
                      <li className="terms-element-list">
                        Confirm the deletion in the dialog that appears. Once confirmed, the
                        deletion request is queued immediately and you will be logged out.
                      </li>
                    </ol>

                    <h5 className="mt-4">
                      <strong>How to delete your account if you no longer have the app</strong>
                    </h5>
                    <p>
                      If you have already uninstalled the app, you can request deletion by
                      writing to <strong>info@ridenrepair.com</strong> from the same email
                      address or with the same registered phone number that is linked to your
                      Ride N Repair account. Include the phrase &quot;Delete my account&quot;
                      in the subject line so that the request is routed correctly. We will
                      verify your identity, action the request, and confirm completion by
                      email within seven (7) working days.
                    </p>

                    <h5 className="mt-4">
                      <strong>What data is deleted</strong>
                    </h5>
                    <p>
                      The following personal data is deleted from our active production
                      systems immediately upon confirmation of your deletion request:
                    </p>
                    <ul>
                      <li className="terms-element-list">Full name and profile photo (if any)</li>
                      <li className="terms-element-list">Phone number and email address</li>
                      <li className="terms-element-list">
                        Saved addresses, geo-location pins, and home / office labels
                      </li>
                      <li className="terms-element-list">
                        Vehicle details — brand, model, registration plate, year, odometer
                      </li>
                      <li className="terms-element-list">
                        Push-notification device tokens (you stop receiving notifications
                        immediately)
                      </li>
                      <li className="terms-element-list">
                        Sentry crash logs and product-analytics events that contain your
                        identifiers
                      </li>
                      <li className="terms-element-list">
                        Ratings, reviews, and free-text feedback authored under your account
                      </li>
                    </ul>

                    <h5 className="mt-4">
                      <strong>What data is retained, and for how long</strong>
                    </h5>
                    <p>
                      A limited subset of data is retained after account deletion because we
                      are legally required to keep it. None of this retained data can be used
                      to log you back in or to send you marketing communications.
                    </p>
                    <ul>
                      <li className="terms-element-list">
                        <strong>Anonymised booking records</strong> — booking id, service
                        type, date, amount paid, mechanic id, and city — kept for{' '}
                        <strong>eight (8) financial years</strong> as required by Section
                        36(1) of the Central Goods and Services Tax (CGST) Act, 2017 and
                        Section 209 of the Companies Act, 2013. These records are stripped of
                        your name, phone, email, address, and vehicle plate before retention.
                      </li>
                      <li className="terms-element-list">
                        <strong>GST tax invoices</strong> issued under your name and
                        related accounting entries — kept for the same{' '}
                        <strong>eight (8) financial years</strong> for statutory tax-audit
                        purposes.
                      </li>
                      <li className="terms-element-list">
                        <strong>Payment-gateway transaction metadata</strong> (Razorpay
                        order id, payment id, amount, status) — retained for{' '}
                        <strong>eight (8) financial years</strong> to satisfy reconciliation,
                        dispute resolution, and chargeback obligations under the Payment and
                        Settlement Systems Act, 2007.
                      </li>
                      <li className="terms-element-list">
                        <strong>Aggregate, non-identifying analytics</strong> (e.g. number of
                        bookings in a city in a month) — retained indefinitely. These records
                        contain no information that can identify you.
                      </li>
                    </ul>

                    <h5 className="mt-4">
                      <strong>Backup retention</strong>
                    </h5>
                    <p>
                      Encrypted database backups may continue to contain your data for up to{' '}
                      <strong>ninety (90) days</strong> after your deletion request is
                      actioned. We do not restore deleted accounts from backups; backups are
                      only ever used to recover the platform from a catastrophic failure, and
                      any restored copy of your data is re-deleted as part of the recovery
                      process.
                    </p>

                    <h5 className="mt-4">
                      <strong>Questions</strong>
                    </h5>
                    <p>
                      If you have any questions about how your data is handled, or you want a
                      copy of the data we hold about you before requesting deletion, contact
                      our Data Protection Officer at <strong>info@ridenrepair.com</strong> or
                      call <strong>+91 120 533 8129</strong>. We respond to all data-rights
                      requests within seven (7) working days.
                    </p>

                    <p className="mt-4">
                      <em>
                        This page is effective as of 25 May 2026. The retention periods above
                        reflect Indian statutory requirements applicable to RIDE N REPAIR
                        PRIVATE LIMITED on that date and may be updated to reflect changes in
                        law.
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </>
    );
  }
}

export default DeleteAccount;
